import React from "react";
import { SliceComponentProps, graphql } from "gatsby";
import { KontentArticleOrSotDPage, KontentRelatedItem } from "../../types";
import { formatImages } from "../../lib/images";
import ArticleSidebar from "../../components/elements/ArticleSidebar/ArticleSidebar";
import { formatArticle } from "../../lib/useArticles";

export interface RelatedItemsSidebarProps extends SliceComponentProps {
  data: {
    scientistPages: {
      edges: {
        node: {
          id: string;
          elements: KontentRelatedItem["elements"];
        };
      }[];
    };
    articlePages: {
      nodes: KontentArticleOrSotDPage[];
    };
  };
  sliceContext: {
    contentType: "scientist" | "article";
  };
}

const RelatedItemsSidebar: React.FC<RelatedItemsSidebarProps> = ({
  data,
  sliceContext,
}) => {
  const { contentType } = sliceContext;

  const relatedItems =
    contentType === "scientist"
      ? data.scientistPages?.edges?.map((edge) => {
          return {
            id: edge.node.id,
            title: edge.node?.elements?.title?.value,
            url: edge.node?.elements?.url?.url,
            category:
              edge.node?.elements?.content?.value[0]?.elements?.content_tile
                ?.value[0]?.elements?.content_category_tax?.value[0]?.name,
            date: edge.node?.elements?.display_publish_date?.value,
            image: formatImages(
              edge.node?.elements?.content?.value[0]?.elements?.content_tile
                ?.value[0]?.elements?.image?.value[0]?.elements
            ),
          };
        })
      : data.articlePages?.nodes
          .map((articlePage) => {
            const articleData = formatArticle(
              articlePage.elements?.content?.value[0]
            );

            const pageInfo = {
              url: articlePage.elements?.url?.url,
              title: articlePage.elements?.title?.value,
              displayPublishDate:
                articlePage.elements?.display_publish_date?.value,
            };

            return {
              page: pageInfo,
              ...articleData,
            };
          })
          .slice(0, 5)
          .map((article) => ({
            title: article.page?.title,
            url: article.page?.url,
            category: article.contentCategory,
            date:
              article.page?.displayPublishDate &&
              new Date(article.page.displayPublishDate).toLocaleDateString(
                "en-us",
                {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                }
              ),
            image: {
              largeImage: article.image,
            },
          }));

  return <ArticleSidebar relatedItems={relatedItems} />;
};

export default RelatedItemsSidebar;

export const RelatedItemsSidebarQuery = graphql`
  query {
    articlePages: allKontentItemPage(
      filter: {
        elements: {
          content: {
            value: {
              elemMatch: { system: { type: { eq: "content___article" } } }
            }
          }
        }
      }
      sort: { elements: { display_publish_date: { value: DESC } } }
      limit: 33
    ) {
      nodes {
        id
        system {
          id
        }
        elements {
          content {
            value {
              ... on kontent_item_content___article {
                id
                elements {
                  image {
                    value {
                      ...kontentMediaImage
                    }
                  }
                  content_tile {
                    value {
                      ...kontentContentTile
                    }
                  }
                  page_metadata__content_category {
                    value {
                      name
                    }
                  }
                }
                system {
                  id
                }
              }
            }
          }
          url {
            url
          }
          title {
            value
          }
          display_publish_date {
            value
          }
        }
      }
    }
    scientistPages: allKontentItemPage(
      filter: {
        elements: {
          content: {
            value: {
              elemMatch: {
                internal: { type: { eq: "kontent_item_content___sotd" } }
              }
            }
          }
        }
      }
      sort: { elements: { display_publish_date: { value: DESC } } }
      limit: 5
    ) {
      edges {
        node {
          id
          elements {
            content {
              value {
                ... on kontent_item_content___sotd {
                  __typename
                  system {
                    name
                  }
                  elements {
                    content_tile {
                      value {
                        ... on kontent_item_element___content_tile {
                          ...kontentContentTile
                        }
                      }
                    }
                  }
                }
              }
            }
            title {
              value
            }
            display_publish_date {
              value(formatString: "MMMM D, YYYY")
            }
            url {
              url
            }
          }
        }
      }
    }
  }
`;
